import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { NAV_SECTIONS } from '../config/navigation';
import NavIcon from './NavIcon';
import './Layout.css';

const Layout = ({ children }) => {
  const { logout, user, hasPermission } = useContext(AuthContext);
  const { companyName, logoUrl } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const isDesktop = () => window.innerWidth > 1024;
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);

  useEffect(() => {
    const handleResize = () => setSidebarOpen(isDesktop());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/sy-admin/login');
  };

  const closeOnMobile = () => {
    if (!isDesktop()) setSidebarOpen(false);
  };

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === '/sy-admin' || location.pathname === '/sy-admin/';
    }
    return location.pathname.startsWith(item.path);
  };

  // Hide a whole section when the user cannot open any item inside it
  const sections = NAV_SECTIONS
    .map((section) => ({ ...section, items: section.items.filter((i) => hasPermission(i.permission)) }))
    .filter((section) => section.items.length > 0);

  const current = sections.flatMap((s) => s.items).find(isActive);
  const initials = (user?.name || user?.username || '?')
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return (
    <div className={`layout ${sidebarOpen ? 'nav-open' : 'nav-closed'}`}>
      <div
        className="sidebar-overlay"
        onClick={() => setSidebarOpen(false)}
        role="presentation"
        aria-hidden="true"
      />

      <aside className="sidebar" aria-label="Main navigation">
        <div className="sidebar-header">
          <Link to="/sy-admin" className="sidebar-brand" onClick={closeOnMobile}>
            {logoUrl ? (
              <img src={logoUrl} alt="" className="brand-logo" />
            ) : (
              <span className="brand-mark" aria-hidden="true">
                {(companyName || 'W').charAt(0)}
              </span>
            )}
            <span className="brand-text">{companyName}</span>
          </Link>
          <button
            type="button"
            className="sidebar-collapse"
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d={sidebarOpen ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav">
          {sections.map((section) => (
            <div className="nav-section" key={section.key}>
              <span className="nav-section-label">{section.label}</span>
              {section.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive(item) ? 'active' : ''}`}
                  title={item.label}
                  onClick={closeOnMobile}
                  aria-current={isActive(item) ? 'page' : undefined}
                >
                  <span className="nav-icon"><NavIcon name={item.icon} /></span>
                  <span className="nav-text">{item.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <span className="user-avatar" aria-hidden="true">{initials}</span>
            <span className="user-meta">
              <span className="user-name">{user?.name || user?.username}</span>
              <span className="user-role">
                {user?.isSuperAdmin ? 'Super Admin' : user?.role?.name || 'No role'}
              </span>
            </span>
          </div>
          <button type="button" onClick={handleLogout} className="nav-item logout-btn" title="Log out">
            <span className="nav-icon"><NavIcon name="logout" /></span>
            <span className="nav-text">Log out</span>
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <button
            type="button"
            className="topbar-menu"
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label="Toggle navigation"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <span className="topbar-title">{current?.label || 'Admin'}</span>
          <span className="topbar-spacer" />
          <span className="topbar-user" title={user?.username}>
            <span className="user-avatar" aria-hidden="true">{initials}</span>
          </span>
        </header>

        <div className="content-wrapper">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
